import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { shipmentsAPI, labelsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { 
  FiUpload, 
  FiFile, 
  FiDownload, 
  FiEye, 
  FiTruck,
  FiPackage,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiRefreshCw,
  FiPlus,
  FiChevronDown,
  FiChevronRight,
  FiUser,
  FiMapPin,
  FiX,
  FiBox,
  FiFileText,
  FiArchive,
  FiEdit
} from 'react-icons/fi';

const Shipments = () => {
  const navigate = useNavigate();
  const [expandedShipments, setExpandedShipments] = useState(new Set());
  const [viewingLabels, setViewingLabels] = useState(null);
  const [editingShipment, setEditingShipment] = useState(null);
  const [editingUnits, setEditingUnits] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('created'); // 'created', 'waiting-labels', 'ready-package', 'in-transit', 'delivered'
  const queryClient = useQueryClient();

  const { data: masterShipments, isLoading: mastersLoading, refetch: refetchMasters } = useQuery(
    'master-shipments',
    () => shipmentsAPI.getMasterShipments()
  );

  // Filter shipments based on active tab
  const getFilteredShipments = () => {
    if (!masterShipments?.results) return [];
    
    switch (activeTab) {
      case 'created':
        return masterShipments.results.filter(s => s.status === 'uploaded');
      case 'waiting-labels':
        return masterShipments.results.filter(s => s.status === 'customer_packaging');
      case 'ready-package':
        return masterShipments.results.filter(s => s.status === 'boxes_created');
      case 'warehouse':
        return masterShipments.results.filter(s => ['ready_for_origin_warehouse', 'received_in_india', 'partially_received_india'].includes(s.status));
      case 'in-transit':
        return masterShipments.results.filter(s => 
          ['in_transit_to_usa', 'received_in_usa', 'partially_received_usa', 'last_mile_in_transit'].includes(s.status)
        );
      case 'delivered':
        return masterShipments.results.filter(s => s.status === 'delivered');
      default:
        return masterShipments.results;
    }
  };

  const filteredShipments = getFilteredShipments();

  // Action functions for different workflow stages
  const requestLabels = async (shipmentId) => {
    try {
      // This would call an API to request labels
      await shipmentsAPI.requestLabels(shipmentId);
      toast.success('Label request submitted successfully!');
      refetchMasters();
    } catch (error) {
      toast.error('Failed to request labels');
      console.error('Error requesting labels:', error);
    }
  };

  const proceedToPackaging = async (shipmentId) => {
    try {
      // This would call an API to proceed to packaging stage
      await shipmentsAPI.proceedToPackaging(shipmentId);
      toast.success('Ready for packaging! You can now create boxes.');
      refetchMasters();
    } catch (error) {
      toast.error('Failed to proceed to packaging');
      console.error('Error proceeding to packaging:', error);
    }
  };

  const navigateToPackaging = (shipmentId) => {
    // Navigate to Package Items page with the specific shipment ID
    navigate(`/package-items?shipment=${shipmentId}`);
  };

  const handleEditShipment = (shipment) => {
    setEditingShipment(shipment);
    setEditingUnits(shipment.units ? [...shipment.units] : []);
  };

  const handleSaveEdit = async () => {
    try {
      if (!editingShipment || !editingUnits.length) {
        toast.error('No changes to save');
        return;
      }

      setIsSaving(true);
      
      // Call the API to update the units
      await shipmentsAPI.updateShipmentUnits(editingShipment.id, editingUnits);
      
      setEditingShipment(null);
      setEditingUnits([]);
      toast.success('Shipment updated successfully!');
      refetchMasters();
    } catch (error) {
      toast.error('Failed to update shipment');
      console.error('Error updating shipment:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingShipment(null);
    setEditingUnits([]);
  };

  const updateUnit = (unitIndex, field, value) => {
    const updatedUnits = [...editingUnits];
    updatedUnits[unitIndex] = {
      ...updatedUnits[unitIndex],
      [field]: value
    };
    setEditingUnits(updatedUnits);
  };

  const toggleShipmentExpansion = (shipmentId) => {
    const newExpanded = new Set(expandedShipments);
    if (newExpanded.has(shipmentId)) {
      newExpanded.delete(shipmentId);
    } else {
      newExpanded.add(shipmentId);
    }
    setExpandedShipments(newExpanded);
  };


  const handleViewLabels = async (shipment) => {
    try {
      if (shipment.label_file_url) {
        // Open the label PDF in a new tab
        window.open(shipment.label_file_url, '_blank');
      } else {
        toast.error('No label file available for this shipment');
      }
    } catch (error) {
      console.error('Error opening label:', error);
      toast.error('Failed to open label');
    }
  };

  const handleDownloadLabels = async (shipment) => {
    try {
      if (shipment.label_file_url) {
        // Open label PDF in a new tab
        window.open(shipment.label_file_url, '_blank');
        toast.success('Label opened in new tab!');
      } else {
        toast.error('No label file available for this shipment');
      }
    } catch (error) {
      console.error('Error opening label:', error);
      toast.error('Failed to open label');
    }
  };

  const handleDownloadAllLabels = async (shipment) => {
    try {
      const labels = [];
      
      // Add master label if exists
      if (shipment.label_file_url) {
        labels.push({
          url: shipment.label_file_url,
          name: `Master_${shipment.master_tracking_number}.pdf`,
          type: 'master'
        });
      }
      
      // Add box labels
      shipment.boxes?.forEach(box => {
        if (box.label_file_url) {
          labels.push({
            url: box.label_file_url,
            name: `Box_${box.box_tracking_number || box.id}.pdf`,
            type: 'box'
          });
        }
      });
      
      // Add unit labels
      shipment.boxes?.forEach(box => {
        box.units?.forEach(unit => {
          if (unit.label_file_url) {
            labels.push({
              url: unit.label_file_url,
              name: `Unit_${unit.child_tracking_number || unit.id}.pdf`,
              type: 'unit'
            });
          }
        });
      });
      
      if (labels.length === 0) {
        toast.error('No labels available for this shipment');
        return;
      }
      
      // Download all labels
      for (const label of labels) {
        const link = document.createElement('a');
        link.href = label.url;
        link.download = label.name;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      toast.success(`Downloaded ${labels.length} label(s)`);
    } catch (error) {
      console.error('Error downloading all labels:', error);
      toast.error('Failed to download labels');
    }
  };

  const handleDownloadBoxLabels = async (box) => {
    try {
      const labels = [];
      
      // Add box label if exists
      if (box.label_file_url) {
        labels.push({
          url: box.label_file_url,
          name: `Box_${box.box_tracking_number || box.id}.pdf`,
          type: 'box'
        });
      }
      
      // Add unit labels in this box
      box.units?.forEach(unit => {
        if (unit.label_file_url) {
          labels.push({
            url: unit.label_file_url,
            name: `Unit_${unit.child_tracking_number || unit.id}.pdf`,
            type: 'unit'
          });
        }
      });
      
      if (labels.length === 0) {
        toast.error('No labels available for this box');
        return;
      }
      
      // Download all labels
      for (const label of labels) {
        const link = document.createElement('a');
        link.href = label.url;
        link.download = label.name;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      toast.success(`Downloaded ${labels.length} label(s) for this box`);
    } catch (error) {
      console.error('Error downloading box labels:', error);
      toast.error('Failed to download box labels');
    }
  };

  const handleDownloadUnitLabel = async (unit) => {
    try {
      if (unit.label_file_url) {
        const link = document.createElement('a');
        link.href = unit.label_file_url;
        link.download = `Unit_${unit.child_tracking_number || unit.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error('No label available for this unit');
      }
    } catch (error) {
      console.error('Error downloading unit label:', error);
      toast.error('Failed to download unit label');
    }
  };

  const handleDownloadShipmentCSV = async (shipment) => {
    try {
      const csvData = [];
      
      // Add header row
      csvData.push([
        'Master Tracking Number',
        'Box ID',
        'Box Tracking Number',
        'Child Tracking Number',
        'Customer Name',
        'Customer Email',
        'Customer Phone',
        'Address',
        'City',
        'State',
        'ZIP',
        'Country',
        'Item Description',
        'HSN Code',
        'Item Value',
        'Reference ID',
        'Weight (g)',
        'Dimensions (cm)',
        'Status'
      ]);
      
      // Add data rows
      shipment.boxes?.forEach(box => {
        if (box.units && box.units.length > 0) {
          box.units.forEach(unit => {
            csvData.push([
              shipment.master_tracking_number,
              box.box_tracking_number || box.id,
              box.carrier_tracking_number || '',
              unit.carrier_tracking_number || '',
              unit.customer_name || '',
              unit.customer_email || '',
              unit.customer_phone || '',
              unit.address_line1 || '',
              unit.city || '',
              unit.state || '',
              unit.zip || '',
              unit.country || '',
              unit.item_description || '',
              unit.hsn_code || '',
              unit.item_value || '',
              unit.reference_id || '',
              unit.weight_gms || '',
              `${unit.length_cm || 0}×${unit.width_cm || 0}×${unit.height_cm || 0}`,
              unit.status || ''
            ]);
          });
        } else {
          // Box without units
          csvData.push([
            shipment.master_tracking_number,
            box.box_tracking_number || box.id,
            box.carrier_tracking_number || '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            box.status || ''
          ]);
        }
      });
      
      // Convert to CSV string
      const csvString = csvData.map(row => 
        row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      // Create and download file
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `shipment_${shipment.master_tracking_number}_tracking_data.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('CSV file downloaded successfully!');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV file');
    }
  };

  const handleExportTrackingNumbers = async (shipment) => {
    try {
      const csvData = [];
      
      // Add header row
      csvData.push([
        'Master Tracking Number',
        'Box Tracking Number',
        'Carrier Tracking Number',
        'Customer Name',
        'Customer Email',
        'Customer Phone',
        'Address',
        'City',
        'State',
        'ZIP',
        'Country',
        'Item Description',
        'Reference ID'
      ]);
      
      // Check if shipment has boxes (warehouse/in-transit stages) or just units (ready-package stage)
      if (shipment.boxes && shipment.boxes.length > 0) {
        // Shipment has boxes - export from boxes
        shipment.boxes.forEach(box => {
          if (box.units && box.units.length > 0) {
            box.units.forEach(unit => {
              csvData.push([
                shipment.master_tracking_number,
                box.box_tracking_number || box.id,
                unit.carrier_tracking_number || unit.child_tracking_number || '',
                unit.customer_name || '',
                unit.customer_email || '',
                unit.customer_phone || '',
                unit.address_line1 || '',
                unit.city || '',
                unit.state || '',
                unit.zip || '',
                unit.country || '',
                unit.item_description || '',
                unit.reference_id || ''
              ]);
            });
          }
        });
      } else if (shipment.units && shipment.units.length > 0) {
        // Shipment has no boxes yet (ready-package stage) - export units directly
        shipment.units.forEach(unit => {
          csvData.push([
            shipment.master_tracking_number,
            '', // No box tracking number yet
            unit.carrier_tracking_number || unit.child_tracking_number || '',
            unit.customer_name || '',
            unit.customer_email || '',
            unit.customer_phone || '',
            unit.address_line1 || '',
            unit.city || '',
            unit.state || '',
            unit.zip || '',
            unit.country || '',
            unit.item_description || '',
            unit.reference_id || ''
          ]);
        });
      }
      
      if (csvData.length <= 1) {
        toast.error('No tracking data available for this shipment');
        return;
      }
      
      // Convert to CSV string
      const csvString = csvData.map(row => 
        row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      // Create and download file
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `tracking_numbers_${shipment.master_tracking_number}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Tracking numbers exported successfully!');
    } catch (error) {
      console.error('Error exporting tracking numbers:', error);
      toast.error('Failed to export tracking numbers');
    }
  };


  const getShipmentStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'status-delivered';
      case 'in_transit':
        return 'status-in-transit';
      case 'pending':
        return 'status-pending';
      case 'exception':
        return 'status-exception';
      case 'ready_for_origin_warehouse':
        return 'status-pending';
      case 'received_in_india':
        return 'status-delivered';
      case 'partially_received_india':
        return 'status-pending';
      case 'in_transit_to_usa':
        return 'status-in-transit';
      case 'received_in_usa':
        return 'status-delivered';
      case 'partially_received_usa':
        return 'status-pending';
      default:
        return 'status-pending';
    }
  };

  const getWarehouseStatusInfo = (status) => {
    switch (status) {
      case 'ready_for_origin_warehouse':
        return {
          location: 'Origin Warehouse (India)',
          status: 'Ready to Receive',
          description: 'Your shipment is ready to be received at our India warehouse',
          color: 'text-purple-600'
        };
      case 'received_in_india':
        return {
          location: 'Origin Warehouse (India)',
          status: 'Received',
          description: 'All boxes have been received at our India warehouse',
          color: 'text-green-600'
        };
      case 'partially_received_india':
        return {
          location: 'Origin Warehouse (India)',
          status: 'Partially Received',
          description: 'Some boxes have been received at our India warehouse',
          color: 'text-yellow-600'
        };
      case 'in_transit_to_usa':
        return {
          location: 'In Transit',
          status: 'Shipped to USA',
          description: 'Your shipment is on its way to our USA warehouse',
          color: 'text-orange-600'
        };
      case 'received_in_usa':
        return {
          location: 'Destination Warehouse (USA)',
          status: 'Received',
          description: 'All boxes have been received at our USA warehouse',
          color: 'text-green-600'
        };
      case 'partially_received_usa':
        return {
          location: 'Destination Warehouse (USA)',
          status: 'Partially Received',
          description: 'Some boxes have been received at our USA warehouse',
          color: 'text-yellow-600'
        };
      default:
        return {
          location: 'Unknown',
          status: 'Processing',
          description: 'Your shipment is being processed',
          color: 'text-gray-600'
        };
    }
  };

  if (mastersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shipments</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your shipments through the complete workflow
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/create-shipment')}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            <FiPlus className="h-4 w-4 mr-2" />
            Upload Orders
          </button>
          <button
            onClick={() => navigate('/create-shipment?mode=csv')}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <FiUpload className="h-4 w-4 mr-2" />
            Upload CSV
          </button>
        </div>
      </div>

      {/* Workflow Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'created', name: 'Created Orders', count: masterShipments?.results?.filter(s => s.status === 'uploaded').length || 0 },
              { id: 'waiting-labels', name: 'Waiting for Labels', count: masterShipments?.results?.filter(s => s.status === 'customer_packaging').length || 0 },
              { id: 'ready-package', name: 'Ready to Package', count: masterShipments?.results?.filter(s => s.status === 'boxes_created').length || 0 },
              { id: 'warehouse', name: 'Warehouse Status', count: masterShipments?.results?.filter(s => ['ready_for_origin_warehouse', 'received_in_india', 'partially_received_india'].includes(s.status)).length || 0 },
              { id: 'in-transit', name: 'In Transit', count: masterShipments?.results?.filter(s => ['in_transit_to_usa', 'received_in_usa', 'partially_received_usa', 'last_mile_in_transit'].includes(s.status)).length || 0 },
              { id: 'delivered', name: 'Delivered', count: masterShipments?.results?.filter(s => s.status === 'delivered').length || 0 }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                {tab.name}
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-900'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Master Shipments */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {activeTab === 'created' && 'Created Orders'}
              {activeTab === 'waiting-labels' && 'Waiting for Labels'}
              {activeTab === 'ready-package' && 'Ready to Package'}
              {activeTab === 'warehouse' && 'Warehouse Status'}
              {activeTab === 'in-transit' && 'In Transit'}
              {activeTab === 'delivered' && 'Delivered'}
            </h3>
            <span className="text-sm text-gray-500">
              {filteredShipments.length} shipments
            </span>
          </div>
          {filteredShipments.length > 0 ? (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Master ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Units
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Labels
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 sm:w-auto">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredShipments.map((shipment) => {
                    const isExpanded = expandedShipments.has(shipment.id);
                    return (
                      <React.Fragment key={shipment.id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div className="flex items-center">
                              <button
                                onClick={() => toggleShipmentExpansion(shipment.id)}
                                className="mr-2 text-gray-400 hover:text-gray-600"
                              >
                                {isExpanded ? (
                                  <FiChevronDown className="h-4 w-4" />
                                ) : (
                                  <FiChevronRight className="h-4 w-4" />
                                )}
                              </button>
                              <div>
                                <div className="font-medium text-gray-900 flex items-center">
                                  <FiTruck className="h-4 w-4 text-gray-400 mr-1" />
                                  {shipment.master_tracking_number}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <FiBox className="h-4 w-4 text-green-400 mr-1" />
                              <span className="text-green-700 font-medium">
                                {(() => {
                                  const nonCancelledUnits = shipment.units?.filter(unit => !unit.is_cancelled) || [];
                                  return nonCancelledUnits.length;
                                })()} Units
                              </span>
                              {shipment.units?.some(unit => unit.is_cancelled) && (
                                <span className="ml-2 text-xs text-red-600">
                                  ({shipment.units?.filter(unit => unit.is_cancelled).length} cancelled)
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {activeTab === 'warehouse' ? (
                              <div className="space-y-2">
                                <div className={`font-medium ${getWarehouseStatusInfo(shipment.status).color}`}>
                                  {getWarehouseStatusInfo(shipment.status).location}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {getWarehouseStatusInfo(shipment.status).status}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {getWarehouseStatusInfo(shipment.status).description}
                                </div>
                              </div>
                            ) : (
                              <span className={`status-badge ${getShipmentStatusColor(shipment.status)}`}>
                                {shipment.status.replace('_', ' ')}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex flex-col space-y-1">
                              <div className="flex items-center">
                                {shipment.has_labels ? (
                                  <>
                                    <FiCheckCircle className="h-4 w-4 text-green-500 mr-2" />
                                    <span className="text-green-700 font-medium">
                                      Generated ({shipment.labels_count})
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <FiClock className="h-4 w-4 text-yellow-500 mr-2" />
                                    <span className="text-yellow-700">
                                      Pending
                                    </span>
                                  </>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {shipment.label_file_url && (
                                  <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded flex items-center">
                                    <FiFileText className="h-3 w-3 mr-1" />
                                    Master
                                  </span>
                                )}
                                {shipment.boxes?.some(box => box.label_file_url) && (
                                  <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded flex items-center">
                                    <FiPackage className="h-3 w-3 mr-1" />
                                    Boxes
                                  </span>
                                )}
                                {shipment.boxes?.some(box => 
                                  box.units?.some(unit => unit.label_file_url)
                                ) && (
                                  <span className="px-2 py-1 text-xs bg-pink-100 text-pink-800 rounded flex items-center">
                                    <FiFileText className="h-3 w-3 mr-1" />
                                    Units
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(shipment.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium">
                            {activeTab === 'created' && (
                              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                                <button
                                  onClick={() => handleEditShipment(shipment)}
                                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
                                >
                                  <FiEdit className="h-3 w-3 mr-1" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => requestLabels(shipment.id)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                                >
                                  <span className="hidden sm:inline">Request Labels</span>
                                  <span className="sm:hidden">Labels</span>
                                </button>
                              </div>
                            )}
                            {activeTab === 'waiting-labels' && (
                              <span className="text-yellow-600 text-xs">Waiting for admin to upload labels</span>
                            )}
                            {activeTab === 'ready-package' && (
                              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                                <button
                                  onClick={() => handleDownloadLabels(shipment)}
                                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
                                  title="Open Labels in New Tab"
                                >
                                  <FiEye className="h-3 w-3 mr-1" />
                                  <span className="hidden sm:inline">View Labels</span>
                                  <span className="sm:hidden">Labels</span>
                                </button>
                                <button
                                  onClick={() => handleExportTrackingNumbers(shipment)}
                                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
                                  title="Export Tracking Numbers"
                                >
                                  <FiFileText className="h-3 w-3 mr-1" />
                                  <span className="hidden sm:inline">Export Tracking</span>
                                  <span className="sm:hidden">Export</span>
                                </button>
                                <button
                                  onClick={() => navigateToPackaging(shipment.id)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 whitespace-nowrap"
                                >
                                  <span className="hidden sm:inline">Start Packaging</span>
                                  <span className="sm:hidden">Package</span>
                                </button>
                              </div>
                            )}
                            {activeTab === 'in-transit' && (
                              <div className="flex items-center">
                                <button 
                                  onClick={() => handleDownloadShipmentCSV(shipment)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                                  title="Download CSV"
                                >
                                  <FiDownload className="h-3 w-3 mr-1" />
                                  <span className="hidden sm:inline">Download CSV</span>
                                  <span className="sm:hidden">CSV</span>
                                </button>
                              </div>
                            )}
                            {activeTab === 'delivered' && (
                              <span className="text-green-600 text-xs">Delivered</span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan="6" className="px-6 py-4 bg-gray-50">
                              <div className="ml-6">
                                {activeTab === 'warehouse' || activeTab === 'in-transit' ? (
                                  <h4 className="text-sm font-medium text-gray-900 mb-3">Packaging Structure</h4>
                                ) : (
                                  <h4 className="text-sm font-medium text-gray-900 mb-3">Order Items (Units)</h4>
                                )}
                                {activeTab === 'warehouse' || activeTab === 'in-transit' ? (
                                  // Show Master -> Box -> Child structure for warehouse tab
                                  <div className="space-y-4">
                                    {shipment.boxes && shipment.boxes.length > 0 ? (
                                      shipment.boxes.map((box, boxIndex) => (
                                        <div key={box.id} className="bg-white p-4 rounded border border-gray-200">
                                          <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center">
                                              <FiPackage className="h-5 w-5 text-blue-500 mr-2" />
                                              <div>
                                                <h5 className="font-medium text-gray-900">{box.box_name}</h5>
                                                <p className="text-sm text-gray-500">Box ID: {box.box_tracking_number}</p>
                                                {box.carrier_tracking_number && (
                                                  <p className="text-xs text-blue-600 font-medium">
                                                    Tracking: {box.carrier_tracking_number}
                                                  </p>
                                                )}
                                              </div>
                                            </div>
                                            <div className="text-right">
                                              <span className={`px-2 py-1 text-xs rounded-full ${
                                                box.status === 'received_in_india' || box.status === 'received_in_usa' 
                                                  ? 'bg-green-100 text-green-800'
                                                  : box.status === 'partially_received_india' || box.status === 'partially_received_usa'
                                                  ? 'bg-yellow-100 text-yellow-800'
                                                  : 'bg-gray-100 text-gray-800'
                                              }`}>
                                                {box.status.replace('_', ' ')}
                                              </span>
                                            </div>
                                          </div>
                                          
                                          {box.units && box.units.length > 0 && (
                                            <div className="ml-6">
                                              <h6 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                                                Units in this box ({box.units.length})
                                              </h6>
                                              <div className="space-y-2">
                                                {box.units.map((unit, unitIndex) => (
                                                  <div key={unit.id} className="bg-gray-50 p-3 rounded border border-gray-100">
                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                                                      <div className="flex items-start">
                                                        <FiUser className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                                        <div>
                                                          <div className="font-medium text-gray-900">{unit.customer_name}</div>
                                                          <div className="text-gray-500">{unit.customer_email}</div>
                                                          <div className="text-gray-500">{unit.customer_phone}</div>
                                                          {unit.carrier_tracking_number && (
                                                            <div className="text-xs text-blue-600 font-medium flex items-center mt-1">
                                                              <FiCheckCircle className="h-3 w-3 mr-1" />
                                                              Tracking: {unit.carrier_tracking_number}
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                      <div className="flex items-start">
                                                        <FiMapPin className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                                        <div>
                                                          <div className="text-gray-900">{unit.address_line1}</div>
                                                          {unit.address_line2 && <div className="text-gray-600">{unit.address_line2}</div>}
                                                          <div className="text-gray-600">{unit.city}, {unit.state} {unit.zip}</div>
                                                          <div className="text-gray-500">{unit.country}</div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-start">
                                                        <FiBox className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                                        <div>
                                                          <div className="font-medium text-gray-900">{unit.item_description}</div>
                                                          <div className="text-gray-500">HSN: {unit.hsn_code}</div>
                                                          <div className="text-gray-500">Value: ${unit.item_value}</div>
                                                          <div className="text-gray-500">Ref: {unit.reference_id}</div>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-start justify-between">
                                                        <div>
                                                          <div className="text-gray-600 font-medium">
                                                            {unit.weight_gms}g
                                                          </div>
                                                          <div className="text-gray-500">
                                                            {unit.length_cm}×{unit.width_cm}×{unit.height_cm}cm
                                                          </div>
                                                          <div className="mt-1">
                                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                              unit.status === 'delivered' ? 'bg-green-100 text-green-800' :
                                                              unit.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                                                              'bg-gray-100 text-gray-800'
                                                            }`}>
                                                              {unit.status.replace('_', ' ')}
                                                            </span>
                                                          </div>
                                                          {unit.label_file_url && (
                                                            <div className="mt-2">
                                                              <span className="px-2 py-1 text-xs bg-pink-100 text-pink-800 rounded flex items-center">
                                                                <FiFileText className="h-3 w-3 mr-1" />
                                                                Unit Label
                                                              </span>
                                                            </div>
                                                          )}
                                                        </div>
                                                        {unit.label_file_url && (
                                                          <div className="flex items-center space-x-1">
                                                            <button 
                                                              onClick={() => window.open(unit.label_file_url, '_blank')}
                                                              className="text-blue-600 hover:text-blue-900 text-xs"
                                                              title="View Unit Label"
                                                            >
                                                              <FiEye className="h-4 w-4" />
                                                            </button>
                                                            <button 
                                                              onClick={() => handleDownloadUnitLabel(unit)}
                                                              className="text-green-600 hover:text-green-900 text-xs"
                                                              title="Download Unit Label"
                                                            >
                                                              <FiDownload className="h-4 w-4" />
                                                            </button>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="text-center py-4 text-gray-500">
                                        <FiPackage className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                        <p>No boxes created yet</p>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  // Show Master -> Child structure for other tabs
                                  <div className="space-y-3">
                                    {/* Non-cancelled units */}
                                    {shipment.units?.filter(unit => !unit.is_cancelled).map((unit, unitIndex) => (
                                    <div key={unit.id} className="bg-white p-4 rounded border border-gray-200">
                                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                                        <div className="flex items-start">
                                          <FiUser className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                          <div>
                                            <div className="font-medium text-gray-900">{unit.customer_name}</div>
                                            <div className="text-gray-500">{unit.customer_email}</div>
                                            <div className="text-gray-500">{unit.customer_phone}</div>
                                            {unit.carrier_tracking_number && (
                                              <div className="text-xs text-blue-600 font-medium flex items-center mt-1">
                                                <FiCheckCircle className="h-3 w-3 mr-1" />
                                                Tracking: {unit.carrier_tracking_number}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-start">
                                          <FiMapPin className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                          <div>
                                            <div className="text-gray-900">{unit.address_line1}</div>
                                            {unit.address_line2 && <div className="text-gray-600">{unit.address_line2}</div>}
                                            <div className="text-gray-600">{unit.city}, {unit.state} {unit.zip}</div>
                                            <div className="text-gray-500">{unit.country}</div>
                                          </div>
                                        </div>
                                        <div className="flex items-start">
                                          <FiBox className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                          <div>
                                            <div className="font-medium text-gray-900">{unit.item_description}</div>
                                            <div className="text-gray-500">HSN: {unit.hsn_code}</div>
                                            <div className="text-gray-500">Value: ${unit.item_value}</div>
                                            <div className="text-gray-500">Ref: {unit.reference_id}</div>
                                          </div>
                                        </div>
                                        <div className="flex items-start justify-between">
                                          <div>
                                            <div className="text-gray-600 font-medium">
                                              {unit.weight_gms}g
                                            </div>
                                            <div className="text-gray-500">
                                              {unit.length_cm}×{unit.width_cm}×{unit.height_cm}cm
                                            </div>
                                            <div className="mt-2">
                                              <span className={`status-badge ${getShipmentStatusColor(unit.status)}`}>
                                                {unit.status.replace('_', ' ')}
                                              </span>
                                            </div>
                                            {unit.label_file_url && (
                                              <div className="mt-2">
                                                <span className="px-2 py-1 text-xs bg-pink-100 text-pink-800 rounded flex items-center">
                                                  <FiFileText className="h-3 w-3 mr-1" />
                                                  Unit Label
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                          {unit.label_file_url && (
                                            <div className="flex items-center space-x-1">
                                              <button 
                                                onClick={() => window.open(unit.label_file_url, '_blank')}
                                                className="text-blue-600 hover:text-blue-900 text-xs"
                                                title="View Unit Label"
                                              >
                                                <FiEye className="h-4 w-4" />
                                              </button>
                                              <button 
                                                onClick={() => handleDownloadUnitLabel(unit)}
                                                className="text-green-600 hover:text-green-900 text-xs"
                                                title="Download Unit Label"
                                              >
                                                <FiDownload className="h-4 w-4" />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    ))}
                                    
                                    {/* Cancelled units section */}
                                    {shipment.units?.some(unit => unit.is_cancelled) && (
                                      <div className="mt-6 pt-4 border-t border-gray-200">
                                        <h5 className="text-sm font-medium text-red-700 mb-3 flex items-center">
                                          <FiAlertCircle className="h-4 w-4 mr-2" />
                                          Cancelled Units ({shipment.units?.filter(unit => unit.is_cancelled).length})
                                        </h5>
                                        <div className="space-y-2">
                                          {shipment.units?.filter(unit => unit.is_cancelled).map((unit, unitIndex) => (
                                            <div key={unit.id} className="bg-red-50 p-3 rounded border border-red-200">
                                              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                                                <div className="flex items-start">
                                                  <FiUser className="h-4 w-4 text-red-400 mr-2 mt-1" />
                                                  <div>
                                                    <div className="font-medium text-red-900">{unit.customer_name}</div>
                                                    <div className="text-red-600">{unit.customer_email}</div>
                                                    <div className="text-red-500">{unit.customer_phone}</div>
                                                  </div>
                                                </div>
                                                <div className="flex items-start">
                                                  <FiMapPin className="h-4 w-4 text-red-400 mr-2 mt-1" />
                                                  <div>
                                                    <div className="text-red-900">{unit.address_line1}</div>
                                                    {unit.address_line2 && <div className="text-red-600">{unit.address_line2}</div>}
                                                    <div className="text-red-600">{unit.city}, {unit.state} {unit.zip}</div>
                                                    <div className="text-red-500">{unit.country}</div>
                                                  </div>
                                                </div>
                                                <div className="flex items-start">
                                                  <FiBox className="h-4 w-4 text-red-400 mr-2 mt-1" />
                                                  <div>
                                                    <div className="font-medium text-red-900">{unit.item_description}</div>
                                                    <div className="text-red-500">HSN: {unit.hsn_code}</div>
                                                    <div className="text-red-500">Value: ${unit.item_value}</div>
                                                    <div className="text-red-500">Ref: {unit.reference_id}</div>
                                                  </div>
                                                </div>
                                                <div className="flex items-start justify-between">
                                                  <div>
                                                    <div className="text-red-600 font-medium">
                                                      {unit.weight_gms}g
                                                    </div>
                                                    <div className="text-red-500">
                                                      {unit.length_cm}×{unit.width_cm}×{unit.height_cm}cm
                                                    </div>
                                                    <div className="mt-1">
                                                      <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                                                        Cancelled
                                                      </span>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6">
              <FiTruck className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {activeTab === 'created' && 'No orders uploaded yet'}
                {activeTab === 'waiting-labels' && 'No orders waiting for labels'}
                {activeTab === 'ready-package' && 'No orders ready for packaging'}
                {activeTab === 'warehouse' && 'No orders in warehouse'}
                {activeTab === 'in-transit' && 'No orders in transit'}
                {activeTab === 'delivered' && 'No orders delivered yet'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeTab === 'created' 
                  ? 'Upload your orders to get started with the shipment process.'
                  : `No shipments are currently in the ${activeTab.replace('-', ' ')} stage.`
                }
              </p>
              {activeTab === 'created' && (
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/create-shipment')}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    <FiPlus className="h-4 w-4 mr-2" />
                    Upload Orders
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Shipment Modal */}
      {editingShipment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Edit Shipment - {editingShipment.master_tracking_number}
                </h3>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Shipment Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Master ID</label>
                      <input
                        type="text"
                        value={editingShipment.master_tracking_number}
                        disabled
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <input
                        type="text"
                        value={editingShipment.status}
                        disabled
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Order Items ({editingUnits.length})</h4>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {editingUnits.map((unit, index) => (
                      <div key={unit.id} className="bg-white p-4 rounded border border-gray-200">
                        <div className="mb-3 flex items-center justify-between">
                          <h5 className="text-sm font-medium text-gray-900">Item {index + 1}</h5>
                          <span className="text-xs text-gray-500">ID: {unit.child_tracking_number}</span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Customer Information */}
                          <div className="space-y-3">
                            <h6 className="text-xs font-medium text-gray-700 uppercase tracking-wide">Customer Details</h6>
                            <div>
                              <label className="block text-xs font-medium text-gray-600">Customer Name</label>
                              <input
                                type="text"
                                value={unit.customer_name || ''}
                                onChange={(e) => updateUnit(index, 'customer_name', e.target.value)}
                                className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600">Email</label>
                              <input
                                type="email"
                                value={unit.customer_email || ''}
                                onChange={(e) => updateUnit(index, 'customer_email', e.target.value)}
                                className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600">Phone</label>
                              <input
                                type="text"
                                value={unit.customer_phone || ''}
                                onChange={(e) => updateUnit(index, 'customer_phone', e.target.value)}
                                className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>

                          {/* Address Information */}
                          <div className="space-y-3">
                            <h6 className="text-xs font-medium text-gray-700 uppercase tracking-wide">Address</h6>
                            <div>
                              <label className="block text-xs font-medium text-gray-600">Address Line 1</label>
                              <input
                                type="text"
                                value={unit.address_line1 || ''}
                                onChange={(e) => updateUnit(index, 'address_line1', e.target.value)}
                                className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600">Address Line 2</label>
                              <input
                                type="text"
                                value={unit.address_line2 || ''}
                                onChange={(e) => updateUnit(index, 'address_line2', e.target.value)}
                                className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600">City</label>
                                <input
                                  type="text"
                                  value={unit.city || ''}
                                  onChange={(e) => updateUnit(index, 'city', e.target.value)}
                                  className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600">State</label>
                                <input
                                  type="text"
                                  value={unit.state || ''}
                                  onChange={(e) => updateUnit(index, 'state', e.target.value)}
                                  className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600">ZIP Code</label>
                                <input
                                  type="text"
                                  value={unit.zip || ''}
                                  onChange={(e) => updateUnit(index, 'zip', e.target.value)}
                                  className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600">Country</label>
                                <input
                                  type="text"
                                  value={unit.country || ''}
                                  onChange={(e) => updateUnit(index, 'country', e.target.value)}
                                  className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Item Information */}
                          <div className="space-y-3">
                            <h6 className="text-xs font-medium text-gray-700 uppercase tracking-wide">Item Details</h6>
                            <div>
                              <label className="block text-xs font-medium text-gray-600">Description</label>
                              <input
                                type="text"
                                value={unit.item_description || ''}
                                onChange={(e) => updateUnit(index, 'item_description', e.target.value)}
                                className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600">HSN Code</label>
                                <input
                                  type="text"
                                  value={unit.hsn_code || ''}
                                  onChange={(e) => updateUnit(index, 'hsn_code', e.target.value)}
                                  className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600">Item Value ($)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={unit.item_value || ''}
                                  onChange={(e) => updateUnit(index, 'item_value', e.target.value)}
                                  className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600">Reference ID</label>
                              <input
                                type="text"
                                value={unit.reference_id || ''}
                                onChange={(e) => updateUnit(index, 'reference_id', e.target.value)}
                                className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>

                          {/* Physical Properties */}
                          <div className="space-y-3">
                            <h6 className="text-xs font-medium text-gray-700 uppercase tracking-wide">Physical Properties</h6>
                            <div>
                              <label className="block text-xs font-medium text-gray-600">Weight (grams)</label>
                              <input
                                type="number"
                                value={unit.weight_gms || ''}
                                onChange={(e) => updateUnit(index, 'weight_gms', e.target.value)}
                                className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600">Length (cm)</label>
                                <input
                                  type="number"
                                  value={unit.length_cm || ''}
                                  onChange={(e) => updateUnit(index, 'length_cm', e.target.value)}
                                  className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600">Width (cm)</label>
                                <input
                                  type="number"
                                  value={unit.width_cm || ''}
                                  onChange={(e) => updateUnit(index, 'width_cm', e.target.value)}
                                  className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600">Height (cm)</label>
                                <input
                                  type="number"
                                  value={unit.height_cm || ''}
                                  onChange={(e) => updateUnit(index, 'height_cm', e.target.value)}
                                  className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md ${
                    isSaving 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Labels View Modal */}
      {viewingLabels && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Labels for {viewingLabels.shipment.master_tracking_number}
                </h3>
                <button
                  onClick={() => setViewingLabels(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                {viewingLabels.labels.map((label, index) => (
                  <div key={label.id} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FiFile className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="font-medium text-gray-900">
                            {label.label_type === 'master' ? 'Master Label' : 'Child Label'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Status: {label.status}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = `/api/labels/download/${label.id}/`;
                          link.download = `label_${label.id}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <FiDownload className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setViewingLabels(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Shipments;
